import type { TrialStatus } from '../api/trial.api';

export type TrialAlertLevel = 'warning' | 'error';

export interface TrialAlert {
  level: TrialAlertLevel;
  title: string;
  message: string;
  /** Test messages used and allowed, for the usage meter. */
  used: number;
  max: number;
  /** Whole days left on the trial; 0 once it has ended. */
  daysLeft: number;
}

/** The warning starts once this share of the allowance (or less) is left. */
export const LOW_MESSAGES_SHARE = 0.2;

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Which trial alert, if any, the dashboard should show. Pure so the thresholds
 * can be tested without a store or a request.
 *
 * In order of severity: an expired trial, then an exhausted message allowance
 * (both stop the agent replying — the backend answers every chat with 403),
 * then a warning once LOW_MESSAGES_SHARE of the allowance is left.
 */
export function getTrialAlert(status: TrialStatus | null): TrialAlert | null {
  if (!status || !status.isTrial) return null;

  const { used, max, remaining } = status.messages;
  const daysLeft = status.expired ? 0 : status.daysLeft;

  if (status.expired) {
    return {
      level: 'error',
      title: 'Your free trial has ended',
      message: 'Your agent has stopped replying to chats. Choose a plan to keep it running.',
      used,
      max,
      daysLeft,
    };
  }

  if (remaining <= 0) {
    return {
      level: 'error',
      title: `You've used all ${plural(max, 'free test message')}`,
      message: "Your agent won't reply to new chats until you choose a plan.",
      used,
      max,
      daysLeft,
    };
  }

  if (remaining <= Math.max(1, Math.ceil(max * LOW_MESSAGES_SHARE))) {
    return {
      level: 'warning',
      title: `Only ${plural(remaining, 'free test message')} left`,
      message: `You've used ${used} of ${max}. Your agent stops replying once they run out.`,
      used,
      max,
      daysLeft,
    };
  }

  return null;
}
